import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react"
import { Keyboard } from '@capacitor/keyboard'
import { isIOS } from '@/lib/platform'
import { ChatHeader } from "@/components/chat/ChatHeader"
import { ChatMessagesContainer } from "@/components/chat/ChatMessagesContainer"
import { ChatInput } from "@/components/chat/ChatInput"
import { BookingAvailabilityDialog } from "@/components/chat/booking/BookingAvailabilityDialog"
import { SimpleAvailabilityDialog } from "@/components/chat/booking/SimpleAvailabilityDialog"
import { OffPlatformWarningDialog } from "@/components/chat/OffPlatformWarningDialog"
import { ChatImagePreviewProvider } from '@/components/chat/ChatImagePreviewContext'
import { BookingBanner } from "@/components/chat/booking/BookingBanner"
import { useChatDetail } from "@/hooks/useChatDetail"
import { useUserData } from "@/hooks/useUserData"
import { AuthModal } from "@/components/auth/AuthModal"
import { useAuthState } from "@/hooks/useAuth"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useBookingSystem } from "@/contexts/BookingSystemContext"
import type { Booking } from "@/contexts/BookingSystemContext"
import type { CachedChatDisplayData } from "@/lib/chatListCache"
import { addBookingSelectionMessageToChat, addSimpleAvailabilityToChat, findExistingChat } from "@/lib/chatData"
import { checkArtistHasBookingSystem } from "@/lib/tattooData"
import { getClientRegistry } from "@/lib/clientRegistryData"
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom"
import { StickToBottom } from "use-stick-to-bottom"

export default function ChatDetailPage() {
  /** Keeps chat attachment sheet under same stacking subtree as the fixed composer (portal defaults to body otherwise). */
  const chatPagePortalHostRef = useRef<HTMLDivElement>(null)
  const { chatId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading: authLoading } = useAuthState()
  const { artistId: currentArtistId, displayName: currentUserDisplayName } = useUserData()
  const { trackAuthPrompt, trackEvent } = useAnalytics()
  const { hasBookingSystem, subscription, subscribeToBookingByChatId } = useBookingSystem()
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showBookingDialog, setShowBookingDialog] = useState(false)
  const [showSimpleBookingDialog, setShowSimpleBookingDialog] = useState(false)
  const [booking, setBooking] = useState<Booking | null>(null)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [artistHasBookingSystem, setArtistHasBookingSystem] = useState(false)
  const [bookingMessageInView, setBookingMessageInView] = useState(false)
  const [showOffPlatformWarning, setShowOffPlatformWarning] = useState(false)
  const [pendingSendCallback, setPendingSendCallback] = useState<(() => Promise<void>) | null>(null)
  const [pendingMatchedKeyword, setPendingMatchedKeyword] = useState<string | null>(null)
  const [cachedDisplayData] = useState<CachedChatDisplayData | null>(
    (location.state as any)?.cachedDisplayData || null
  )
  
  
  const trackedAvailabilityReadRef = useRef<Set<string>>(new Set())
  
  const {
    // State
    artist,
    chatUser,
    otherPartyLoading,
    chat,
    chatLoading,
    message,
    setMessage,
    isNewChat,
    isNewChatCreated,
    pendingImages,
    attachedArtistPortfolioItems,
    isArtist,
    artistChatSettings,
    artistSubscriptionStatus,
    otherPartyIsTyping,
    
    // Helper functions
    getOtherPartyDisplayName,
    getOtherPartySubtitle,
    flash,
    
    // Event handlers
    handleBackClick,
    handleSendMessage,
    handleImageSelect,
    handleRemoveImage,
    handleRetryMessage,
    handleRetryImageUpload,
    replaceAttachedArtistPortfolioSelection,
    removeAttachedArtistPortfolioItemById,
    messageReplyDraft,
    clearMessageReplyDraft,
    beginReplyToMessage,
    beginReplyToImageBurst,
    getReplyAuthorDisplayLabel,
  } = useChatDetail(cachedDisplayData)

  const artistPortfolioReferenceSheetFirstName =
    !isArtist
      ? (artist?.firstName?.trim()
        ? artist.firstName.trim()
        : artist?.displayName?.split(/\s+/).filter(Boolean)[0]?.trim())
      : undefined

  const replyComposerPreview = useMemo(
    () =>
      messageReplyDraft
        ? {
            authorLabel: getReplyAuthorDisplayLabel(messageReplyDraft),
            bodyPreview: messageReplyDraft.previewText,
          }
        : null,
    [messageReplyDraft, getReplyAuthorDisplayLabel]
  )

  /** Keeps the last messages visible above the fixed composer when it grows (reply strip, attachments, options). */
  const composerDockRef = useRef<HTMLDivElement>(null)
  const [composerDockHeightPx, setComposerDockHeightPx] = useState(120)

  useLayoutEffect(() => {
    const dockElement = composerDockRef.current
    if (!dockElement) return

    const syncComposerDockHeight = () => {
      setComposerDockHeightPx(dockElement.getBoundingClientRect().height)
    }

    syncComposerDockHeight()

    const resizeObserver = new ResizeObserver(syncComposerDockHeight)
    resizeObserver.observe(dockElement)

    window.addEventListener('resize', syncComposerDockHeight)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', syncComposerDockHeight)
    }
  }, [])

  // Find the most recent booking message (for observing and scrolling)
  const mostRecentBookingMessage = useMemo(() => {
    if (!chat?.messages) {
      console.log('[ChatDetailPage] No chat messages to search for booking message')
      return null
    }
    
    // Find the last booking_selection or simple_availability message
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      const msg = chat.messages[i]
      if (msg.type === 'booking_selection' || msg.type === 'simple_availability') {
        console.log('[ChatDetailPage] Found most recent booking message:', {
          messageId: msg.id,
          type: msg.type,
          index: i
        })
        return msg
      }
    }
    console.log('[ChatDetailPage] No booking message found in chat')
    return null
  }, [chat?.messages])

  // Determine the state of the booking button based on message and booking status
  const bookingButtonState = useMemo(() => {
    console.log('[ChatDetailPage] Computing booking button state:', {
      hasMostRecentBookingMessage: !!mostRecentBookingMessage,
      mostRecentMessageId: mostRecentBookingMessage?.id,
      hasBooking: !!booking,
      bookingMessageId: booking?.messageId,
      bookingStatus: booking?.status,
      messageInView: bookingMessageInView
    })

    // No booking message exists at all - allow opening dialog
    if (!mostRecentBookingMessage) {
      console.log('[ChatDetailPage] No booking message - allow opening dialog')
      return { disabled: false, action: 'open_dialog' as const }
    }

    // Check if the most recent booking message has a linked booking
    const messageHasLinkedBooking = booking && booking.messageId === mostRecentBookingMessage.id
    
    console.log('[ChatDetailPage] Message has linked booking?', messageHasLinkedBooking)

    // If no booking linked to this message, it's a new message waiting for booking
    if (!messageHasLinkedBooking) {
      // Also check for legacy bookings without messageId
      const hasLegacyBooking = booking && !booking.messageId
      
      if (hasLegacyBooking) {
        console.log('[ChatDetailPage] Legacy booking detected - treating as linked')
        // Treat legacy booking as linked for backward compatibility
        const bookingIsActive = booking.status !== 'completed' && booking.status !== 'no_show' && booking.status !== 'cancelled'
        
        if (bookingIsActive) {
          if (bookingMessageInView) {
            console.log('[ChatDetailPage] Legacy booking active, message in view - disable button')
            return { disabled: true, action: 'none' as const }
          } else {
            console.log('[ChatDetailPage] Legacy booking active, message not in view - allow scroll')
            return { disabled: false, action: 'scroll_to_message' as const }
          }
        } else {
          console.log('[ChatDetailPage] Legacy booking completed - allow opening dialog')
          return { disabled: false, action: 'open_dialog' as const }
        }
      }

      // New message exists but no booking yet - disable if in view, scroll if not
      if (bookingMessageInView) {
        console.log('[ChatDetailPage] New message in view, no booking yet - disable button')
        return { disabled: true, action: 'none' as const }
      } else {
        console.log('[ChatDetailPage] New message not in view, no booking yet - allow scroll')
        return { disabled: false, action: 'scroll_to_message' as const }
      }
    }

    // Booking exists for this message - check its status
    const bookingIsActive = booking.status !== 'completed' && booking.status !== 'no_show' && booking.status !== 'cancelled'
    
    console.log('[ChatDetailPage] Booking is active?', bookingIsActive)

    if (bookingIsActive) {
      // Active booking - disable if in view, scroll if not
      if (bookingMessageInView) {
        console.log('[ChatDetailPage] Active booking, message in view - disable button')
        return { disabled: true, action: 'none' as const }
      } else {
        console.log('[ChatDetailPage] Active booking, message not in view - allow scroll')
        return { disabled: false, action: 'scroll_to_message' as const }
      }
    } else {
      // Booking is completed/no_show/cancelled - allow creating new booking
      console.log('[ChatDetailPage] Booking completed/cancelled - allow opening dialog')
      return { disabled: false, action: 'open_dialog' as const }
    }
  }, [mostRecentBookingMessage, booking, bookingMessageInView])
  
  // Legacy flags for backward compatibility with send handlers
  const hasBookingSelectionMessage = !!chat?.messages?.some(m => m.type === 'booking_selection')
  const hasSimpleAvailability = !!chat?.messages?.some(m => m.type === 'simple_availability')
  const hasActiveBooking = booking && booking.status !== 'completed' && booking.status !== 'no_show' && booking.status !== 'cancelled'

  // Prepare welcome message for display in new chats
  // Only show welcome message if artist's subscription is active
  const subscriptionIsActive = artistSubscriptionStatus === 'active' || artistSubscriptionStatus === 'trial'
  const welcomeMessage = (subscriptionIsActive && artistChatSettings?.welcomeMessage) ? {
    content: artistChatSettings.welcomeMessage.content || ''
  } : null

  // Check for existing chat when navigating to a new chat with an artist
  useEffect(() => {
    const checkExistingChat = async () => {
      // Only check for existing chats when:
      // 1. It's a new chat
      // 2. User is authenticated
      // 3. ArtistId is provided in URL
      // 4. User is not an artist (artists don't initiate chats this way)
      const artistIdFromParams = searchParams.get('artistId')
      const sourceParam = searchParams.get('source')
      
      console.log('[ChatDetailPage] Checking for existing chat:', {
        isNewChat,
        hasUser: !!user,
        artistIdFromParams,
        isArtist,
        source: sourceParam
      })
      
      if (!isNewChat || !user || !artistIdFromParams || isArtist) {
        console.log('[ChatDetailPage] Skipping existing chat check - conditions not met')
        return
      }

      try {
        const existingChatId = await findExistingChat(user.uid, artistIdFromParams)
        if (existingChatId) {
          console.log('[ChatDetailPage] Found existing chat, redirecting to:', existingChatId)
          // Preserve query params (source, flashId, etc.) when redirecting
          const currentParams = new URLSearchParams(window.location.search)
          currentParams.delete('artistId') // Remove artistId from params as it's not needed
          const queryString = currentParams.toString()
          const redirectUrl = `/chats/${existingChatId}${queryString ? `?${queryString}` : ''}`
          navigate(redirectUrl, { replace: true })
        } else {
          console.log('[ChatDetailPage] No existing chat found, will create new one when user sends message')
        }
      } catch (error) {
        console.error('[ChatDetailPage] Error checking for existing chat:', error)
        // Continue with new chat creation on error
      }
    }

    void checkExistingChat()
  }, [isNewChat, user, searchParams, isArtist, navigate])

  // Show auth modal if user is not logged in (for new chats)
  // Wait for chat UI and Firebase auth resolution before showing modal
  useEffect(() => {
    console.log('[ChatDetailPage] Auth modal trigger check:', {
      user: !!user,
      authLoading,
      isNewChat,
      otherPartyLoading,
    })
    if (!authLoading && !user && isNewChat && !otherPartyLoading) {
      console.log('[ChatDetailPage] Conditions met, showing auth modal')
      trackAuthPrompt('chat_detail', 'auto_prompt')
      setShowLoginModal(true)
    }
  }, [user, authLoading, isNewChat, otherPartyLoading, trackAuthPrompt])

  // Clear modal if user becomes authenticated
  useEffect(() => {
    if (user) {
      setShowLoginModal(false)
    }
  }, [user])

  // Don't switch from cache - just keep using it! It's already correct and loaded instantly.
  // The real data will load in background but we keep showing cache to avoid flicker.
  // This is the whole point - instant rendering without layout shifts.
  // The cache was computed from the exact same source data, so it's always accurate.

  // iOS only: manual translateY above keyboard. Android already insets the WebView
  // (MainActivity decorFitsSystemWindows + Capacitor keyboard resize); tracking here double-counts.
  useEffect(() => {
    if (!isIOS()) return

    let showListener: { remove: () => Promise<void> } | undefined
    let hideListener: { remove: () => Promise<void> } | undefined

    const setup = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setup()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  // Subscribe to booking updates in real-time
  useEffect(() => {
    // Reset if no valid chatId
    if (!chatId || chatId === 'new') {
      setBooking(null)
      setArtistHasBookingSystem(false)
      return
    }

    console.log('[ChatDetailPage] Setting up booking subscription for chatId:', chatId)
    
    // Subscribe to booking changes
    const unsubscribe = subscribeToBookingByChatId(chatId, (updatedBooking) => {
      console.log('[ChatDetailPage] Booking subscription update:', {
        bookingExists: !!updatedBooking,
        bookingId: updatedBooking?.id,
        status: updatedBooking?.status,
        messageId: updatedBooking?.messageId,
        chatId: updatedBooking?.chatId
      })
      setBooking(updatedBooking)
    })

    return () => {
      console.log('[ChatDetailPage] Cleaning up booking subscription')
      unsubscribe()
    }
  }, [chatId, subscribeToBookingByChatId])

  // Check if the viewed artist has booking system access
  // This checks the artist's own booking system status, not the current user's
  useEffect(() => {
    let isMounted = true
    
    const checkArtistBookingAccess = async () => {
      if (isArtist || !artist) {
        if (isMounted) {
          setArtistHasBookingSystem(false)
        }
        return
      }

      try {
        // Check if this specific artist has booking system access
        const hasAccess = await checkArtistHasBookingSystem(artist.id)
        if (isMounted) {
          setArtistHasBookingSystem(hasAccess)
          console.log(`[ChatDetailPage] Artist ${artist.displayName} has booking system:`, hasAccess)
        }
      } catch (error) {
        console.error('[ChatDetailPage] Error checking artist booking system:', error)
        if (isMounted) {
          setArtistHasBookingSystem(false)
        }
      }
    }

    checkArtistBookingAccess()
    return () => { isMounted = false }
  }, [artist, isArtist])

  // Track visibility of booking messages using IntersectionObserver
  useEffect(() => {
    if (!mostRecentBookingMessage) {
      console.log('[ChatDetailPage] No booking message to observe')
      setBookingMessageInView(false)
      return
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const isIntersecting = entry.isIntersecting
        console.log('[ChatDetailPage] Booking message visibility changed:', {
          messageId: mostRecentBookingMessage.id,
          isIntersecting,
          intersectionRatio: entry.intersectionRatio
        })
        setBookingMessageInView(isIntersecting)
      })
    }

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Consider in view when 50% visible
    })

    // Observe the most recent booking message by its unique ID
    const messageId = mostRecentBookingMessage.id
    const messageType = mostRecentBookingMessage.type
    const elementId = messageType === 'booking_selection' 
      ? `booking-selection-message-${messageId}`
      : `simple-availability-message-${messageId}`
    
    console.log('[ChatDetailPage] Looking for booking message element:', {
      messageId,
      messageType,
      elementId
    })
    
    const element = document.getElementById(elementId)
    
    if (element) {
      console.log('[ChatDetailPage] Found booking message element, starting observation')
      observer.observe(element)
    } else {
      console.warn('[ChatDetailPage] Could not find booking message element with id:', elementId)
    }

    return () => {
      observer.disconnect()
    }
  }, [mostRecentBookingMessage])

  // Track when a user actually sees a new availability message in chat
  useEffect(() => {
    if (isArtist || !chat || !mostRecentBookingMessage || !bookingMessageInView) return
    if (mostRecentBookingMessage.senderType !== 'artist') return
    if (mostRecentBookingMessage.type !== 'booking_selection' && mostRecentBookingMessage.type !== 'simple_availability') return
    if (mostRecentBookingMessage.type === 'simple_availability' && mostRecentBookingMessage.simpleBookingData?.isConfirmed) return

    const messageKey = `${chat.id}:${mostRecentBookingMessage.id}`
    if (trackedAvailabilityReadRef.current.has(messageKey)) return
    trackedAvailabilityReadRef.current.add(messageKey)

    void trackEvent('chat_availability_read', {
      chat_id: chat.id,
      artist_id: chat.artistId,
      mode: mostRecentBookingMessage.type === 'booking_selection' ? 'booking_system' : 'simple',
      message_id: mostRecentBookingMessage.id
    })
  }, [bookingMessageInView, chat, isArtist, mostRecentBookingMessage, trackEvent])


  // Memoized callbacks to prevent unnecessary re-renders of child components
  const handleLoginSuccess = useCallback(async () => {
    setShowLoginModal(false)
  }, [])

  // Input/footer is no longer sticky; no need to measure its height

  const handleCalendarClick = useCallback(() => {
    console.log('[ChatDetailPage] Calendar button clicked:', {
      buttonState: bookingButtonState,
      hasBookingMessage: !!mostRecentBookingMessage,
      messageInView: bookingMessageInView,
      booking: booking,
      bookingStatus: booking?.status
    })
    
    // Handle based on computed button state
    switch (bookingButtonState.action) {
      case 'none':
        // Button should be disabled - do nothing
        console.log('[ChatDetailPage] Button action is "none" - should be disabled')
        return
      
      case 'scroll_to_message':
        // Scroll to the existing booking message
        console.log('[ChatDetailPage] Scrolling to existing booking message')
        if (mostRecentBookingMessage) {
          const messageId = mostRecentBookingMessage.id
          const messageType = mostRecentBookingMessage.type
          const elementId = messageType === 'booking_selection' 
            ? `booking-selection-message-${messageId}`
            : `simple-availability-message-${messageId}`
          
          const element = document.getElementById(elementId)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          } else {
            console.warn('[ChatDetailPage] Could not find element to scroll to:', elementId)
          }
        }
        return
      
      case 'open_dialog':
        // Open the appropriate booking dialog
        console.log('[ChatDetailPage] Opening booking dialog', {
          hasBookingSystem,
          subscriptionStatus: subscription?.status
        })
        // Only use booking system if subscription is active or trial
        const subscriptionIsActive = subscription?.status === 'active' || subscription?.status === 'trial'
        if (hasBookingSystem && subscriptionIsActive) {
          setShowBookingDialog(true)
        } else {
          setShowSimpleBookingDialog(true)
        }
        return
    }
  }, [bookingButtonState, mostRecentBookingMessage, hasBookingSystem, subscription, bookingMessageInView, booking])

  const handleSendAvailability = useCallback(async (estimatedTime: number | 'unknown', earliestDate: Date, preparationDays?: number) => {
    if (!chatId || !user || chatId === 'new') return
    if (hasBookingSelectionMessage && hasActiveBooking) return
    
    try {
      const availabilityArtistId = currentArtistId || chat?.artistId || artist?.id
      await addBookingSelectionMessageToChat(
        chatId,
        estimatedTime,
        earliestDate,
        user.uid,
        'artist',
        preparationDays
      )
      if (availabilityArtistId) {
        await trackEvent('chat_availability_sent', {
          chat_id: chatId,
          artist_id: availabilityArtistId,
          mode: 'booking_system',
          estimated_time: estimatedTime,
          preparation_days: preparationDays
        })
      } else {
        console.warn('[ChatDetailPage] Missing artist_id for chat_availability_sent', {
          chatId,
          currentArtistId,
          chatArtistId: chat?.artistId,
          viewedArtistId: artist?.id,
          isArtist
        })
      }
      setShowBookingDialog(false)
    } catch (error) {
      console.error('Error sending booking availability:', error)
    }
  }, [artist?.id, chat?.artistId, chatId, currentArtistId, hasActiveBooking, hasBookingSelectionMessage, isArtist, trackEvent, user])

  const handleSendSimpleAvailability = useCallback(async (
    priceRangeMin: number,
    priceRangeMax: number,
    availableDates: Date[],
    estimatedTime?: number | 'unknown',
    availableTimeSlots?: Record<string, string[]>
  ) => {
    if (!chatId || !user || chatId === 'new') return
    if (hasSimpleAvailability && hasActiveBooking) return
    
    try {
      const availabilityArtistId = currentArtistId || chat?.artistId || artist?.id
      await addSimpleAvailabilityToChat(
        chatId,
        priceRangeMin,
        priceRangeMax,
        availableDates,
        estimatedTime,
        user.uid,
        'artist',
        availableTimeSlots
      )
      if (availabilityArtistId) {
        await trackEvent('chat_availability_sent', {
          chat_id: chatId,
          artist_id: availabilityArtistId,
          mode: 'simple',
          price_min: priceRangeMin,
          price_max: priceRangeMax,
          estimated_time: estimatedTime
        })
      } else {
        console.warn('[ChatDetailPage] Missing artist_id for chat_availability_sent', {
          chatId,
          currentArtistId,
          chatArtistId: chat?.artistId,
          viewedArtistId: artist?.id,
          isArtist
        })
      }
      setShowSimpleBookingDialog(false)
    } catch (error) {
      console.error('Error sending simple availability:', error)
    }
  }, [artist?.id, chat?.artistId, chatId, currentArtistId, hasActiveBooking, hasSimpleAvailability, isArtist, trackEvent, user])

  const handleArtistClick = useCallback(() => {
    if (artist?.id) {
      navigate(`/artist/${artist.id}`)
    }
  }, [artist?.id, navigate])

  const handleUserClick = useCallback(async () => {
    // Artist clicking on the user's name - check for client registry
    console.log('[ChatDetailPage] handleUserClick called:', { isArtist, hasUser: !!chatUser, hasCurrentArtistId: !!currentArtistId, hasBookingSystem })
    
    if (!isArtist || !chatUser || !currentArtistId) {
      console.log('[ChatDetailPage] handleUserClick conditions not met, returning')
      return
    }

    // Only allow user click for artists with booking system
    if (!hasBookingSystem) {
      console.log('[ChatDetailPage] Artist does not have booking system, returning')
      return
    }

    console.log('[ChatDetailPage] Checking for registry with:', { artistId: currentArtistId, userId: chatUser.uid })
    
    try {
      const registry = await getClientRegistry(currentArtistId, chatUser.uid)
      console.log('[ChatDetailPage] Registry lookup result:', { found: !!registry, registryId: registry?.id })
      
      if (registry) {
        // Navigate to the client detail page
        console.log('[ChatDetailPage] Navigating to client detail page')
        navigate(`/booking-system/clients/${chatUser.uid}`)
      } else {
        console.log('[ChatDetailPage] No registry found for this user')
      }
    } catch (error) {
      console.error('Error checking client registry:', error)
    }
  }, [isArtist, chatUser, currentArtistId, navigate, hasBookingSystem])

  // Memoize onBookingCreated callback to prevent ChatMessagesContainer re-renders
  const handleBookingCreated = useCallback(async () => {
    // The booking subscription will automatically update the state
    // This callback is kept for any additional side effects if needed in the future
    console.log('[ChatDetailPage] Booking created/updated - subscription will handle state update')
  }, [])

  // Handle off-platform warning
  const handleOffPlatformWarning = useCallback((callback: () => Promise<void>, matchedKeyword: string) => {
    setPendingSendCallback(() => callback)
    setPendingMatchedKeyword(matchedKeyword)
    setShowOffPlatformWarning(true)
  }, [])

  const handleConfirmOffPlatform = useCallback(async () => {
    if (pendingSendCallback) {
      // Log the off-platform warning acknowledgment
      await trackEvent('chat_off_platform_warning_acknowledged', {
        chat_id: chatId || undefined,
        artist_id: artist?.id || currentArtistId || undefined,
        artist_display_name: artist?.displayName || undefined,
        matched_keyword: pendingMatchedKeyword || '',
      })
      
      await pendingSendCallback()
      setPendingSendCallback(null)
      setPendingMatchedKeyword(null)
    }
  }, [pendingSendCallback, pendingMatchedKeyword, trackEvent, chatId, artist?.id, artist?.displayName, currentArtistId])

  const handleCancelOffPlatform = useCallback(() => {
    setPendingSendCallback(null)
    setPendingMatchedKeyword(null)
  }, [])




  return (
    <ChatImagePreviewProvider>
    <div ref={chatPagePortalHostRef} className="min-h-screen flex flex-col bg-background">
      {/* Frosted header — same pattern as Feed/Artists (notch safe area + md TopNav offset) */}
      <div className="sticky-header-notch-backdrop sticky top-0 shrink-0 md:top-[52px] z-40 bg-background/95 backdrop-blur-md border-b border-border/50 dark:border-border/30 rounded-b-2xl relative">
        <div className="phone-container-padded pt-0 pb-3">
          <ChatHeader
            isArtist={isArtist}
            artist={artist}
            chatUser={chatUser}
            otherPartyLoading={otherPartyLoading}
            onBackClick={handleBackClick}
            onArtistClick={!isArtist ? handleArtistClick : undefined}
            onUserClick={isArtist ? handleUserClick : undefined}
            showBookButton={isArtist}
            onBookClick={handleCalendarClick}
            bookButtonDisabled={bookingButtonState.disabled}
            cachedDisplayData={cachedDisplayData}
            getOtherPartyDisplayName={useMemo(() => () => {
              if (cachedDisplayData) {
                return cachedDisplayData.displayName
              }

              if (isArtist && chatUser) {
                if (chatUser.instagram) {
                  const handle = chatUser.instagram.startsWith('@') ? chatUser.instagram.substring(1) : chatUser.instagram
                  return handle
                }
                if (chatUser.displayName) return chatUser.displayName
                if (chatUser.email) return chatUser.email
                return 'User'
              }

              if (!isArtist && artist) {
                return artist.displayName || 'Artist'
              }

              return getOtherPartyDisplayName()
            }, [isArtist, chatUser, artist, getOtherPartyDisplayName, cachedDisplayData])}
            getOtherPartySubtitle={useMemo(() => getOtherPartySubtitle, [])}
          />
        </div>
        {hasActiveBooking && (
          <BookingBanner
            date={(() => {
              const d = (booking.scheduledTimestamp as any)?.toDate ? (booking.scheduledTimestamp as any).toDate() : new Date(booking.scheduledTimestamp as any)
              return d
            })()}
          />
        )}
      </div>


      {/* Messages Container - Fills remaining space with padding for fixed input */}
      <StickToBottom 
        className={"flex-1 overflow-y-auto " + (booking ? "pt-16 " : "")}
        resize="smooth"
        initial="smooth"
        style={{ 
          paddingBottom: composerDockHeightPx,
          transition: 'padding-bottom 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      > 
        <ChatMessagesContainer
          isNewChat={isNewChat}
          isNewChatCreated={isNewChatCreated}
          chatLoading={chatLoading}
          chat={chat}
          artist={artist}
          chatUser={chatUser}
          flashId={flash?.id}
          onBookingCreated={handleBookingCreated}
          welcomeMessage={welcomeMessage}
          chatSettings={artistChatSettings}
          keyboardHeight={keyboardHeight}
          otherPartyIsTyping={otherPartyIsTyping}
          isArtist={isArtist}
          cachedDisplayData={cachedDisplayData}
          onRetryMessage={handleRetryMessage}
          onReplyToMessage={beginReplyToMessage}
          onReplyToImageBurst={beginReplyToImageBurst}
          getReplyQuoteAuthorLabel={getReplyAuthorDisplayLabel}
        />
      </StickToBottom>

      {/* Radix modal sets body pointer-events:none — restore hits on the fixed composer */}
      <div 
        ref={composerDockRef}
        className="pointer-events-auto fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border/50 dark:border-border/30 rounded-t-2xl"
        style={{
          transform: keyboardHeight > 0 ? `translateY(-${keyboardHeight}px)` : 'translateY(0)',
          transition: 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        <div className="phone-container-padded">
          <ChatInput
            attachmentSheetPortalHostRef={chatPagePortalHostRef}
            message={message}
            setMessage={setMessage}
            pendingImages={pendingImages}
            onSendMessage={handleSendMessage}
            onImageSelect={handleImageSelect}
            onRemoveImage={handleRemoveImage}
            onRetryImageUpload={handleRetryImageUpload}
            autoFocusComposer={false}
            keyboardVisible={keyboardHeight > 0}
            onOffPlatformWarning={handleOffPlatformWarning}
            artistId={artist?.id}
            artistDisplayName={isArtist ? (currentUserDisplayName || undefined) : artist?.displayName}
            attachedArtistPortfolioItems={attachedArtistPortfolioItems}
            replaceAttachedArtistPortfolioSelection={replaceAttachedArtistPortfolioSelection}
            removeAttachedArtistPortfolioItemById={removeAttachedArtistPortfolioItemById}
            artistSheetReferenceFirstName={artistPortfolioReferenceSheetFirstName ?? null}
            initialFlashId={flash?.id}
            replyComposerPreview={replyComposerPreview}
            onDismissReplyComposerPreview={clearMessageReplyDraft}
          />
        </div>
      </div>


      {/* Dialogs */}
      
      {/* Auth Modal - shown when user is not logged in - non-dismissible since chat requires auth */}
      <AuthModal 
        isOpen={showLoginModal}
        onOpenChange={setShowLoginModal}
        dismissible={false}
        defaultMode="signup"
        source="chat_detail"
        onSuccess={handleLoginSuccess}
        artistDisplayName={artist?.displayName}
        artistFirstName={artist?.firstName}
        artistLastName={artist?.lastName}
        artistHasBookingSystem={artistHasBookingSystem}
      />

      {/* Booking Availability Dialog (for artists with booking system) */}
      <BookingAvailabilityDialog
        isOpen={showBookingDialog}
        onOpenChange={setShowBookingDialog}
        onSendAvailability={handleSendAvailability}
        onRecordBooking={(bookingDate, bookingTime) => {
          console.log('[ChatDetailPage] Booking recorded:', bookingDate, bookingTime)
        }}
        chatId={chatId && chatId !== 'new' ? chatId : undefined}
        artistId={isArtist ? currentArtistId || undefined : artist?.id}
        onBookingCreated={handleBookingCreated}
      />

      {/* Simple Availability Dialog (for artists without booking system) */}
      <SimpleAvailabilityDialog
        isOpen={showSimpleBookingDialog}
        onOpenChange={setShowSimpleBookingDialog}
        onSendOffer={handleSendSimpleAvailability}
        chatId={chatId && chatId !== 'new' ? chatId : undefined}
        artistId={isArtist ? currentArtistId || undefined : artist?.id}
        onBookingCreated={handleBookingCreated}
      />

      {/* Off-Platform Warning Dialog */}
      <OffPlatformWarningDialog
        isOpen={showOffPlatformWarning}
        onOpenChange={setShowOffPlatformWarning}
        onConfirm={handleConfirmOffPlatform}
        onCancel={handleCancelOffPlatform}
      />
    </div>
    </ChatImagePreviewProvider>
  )
} 
